import * as React from "react";
import Box from "@mui/material/Box";
import * as Yup from "yup";
import {
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import StyledGrid from "../../shared/components/StyledGrid";
import {
  useBlockUserMutation,
  useChangePasswordMutation,
  useGetAssociationsQuery,
  useGetRolesQuery,
  useGetUsersMutation,
} from "../../services/UserService";
import useFetchWithMsal from "../../utils/useFetchWithMsal";
import {
  ButtonsContainer,
  CopyIcon,
  FormContainer,
  ResetPassworButton,
} from "./RoleAssignmentPage.styled";
import { useSelector } from "react-redux";
import { InfoPopup } from "../../shared/components/InfoPopup";
import { useState } from "react";
import { ModalWindow } from "../../shared/components/modalWindow/ModalWindow";
import { Form, Formik } from "formik";
import InputField from "../../components/formFields/InputField";
import { CreateUserPopup } from "./createUserPopup/CreateUserPopup";
import { LoadingPanel } from "../../shared/components/LoadingPanel";
import { TableFilter } from "../../components/tableFilter/TableFilter";
import { useEffect } from "react";

const RoleAssignmentPage = () => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [sortingModel, setSortingModel] = useState();
  const userId = useSelector((state) => state?.user?.userId);
  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const [isOpenCreateUserPopup, setIsOpenCreateUserPopup] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isResetPasswodWindowOpen, setIsResetPasswodWindowOpen] =
    useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const { skip } = useFetchWithMsal();
  const [getUsers, { data, isLoading }] = useGetUsersMutation("", { skip });
  const { data: roles } = useGetRolesQuery("", {
    skip,
  });
  const { data: associations } = useGetAssociationsQuery("", {
    skip,
  });
  const [blockUser] = useBlockUserMutation();
  const [changePassword] = useChangePasswordMutation();

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, "Password must be at least 8 characters long")
      .max(64, "Password cannot be more than 64 characters long")
      .test(
        "password-strength",
        "Password must have at least 3 of the following: a lowercase letter, an uppercase letter, a digit, or a symbol",
        (value) => {
          if (!value) return false;
          const checks = [
            /[a-z]/.test(value),
            /[A-Z]/.test(value),
            /\d/.test(value),
            /[\W_]/.test(value),
          ];
          return checks.filter(Boolean).length >= 3;
        }
      ),
    confirmPassword: Yup.string()
      .required("Please confirm your password")
      .oneOf([Yup.ref("password")], "Passwords must match"),
  });

  useEffect(() => {
    if (!skip) {
      fetchData();
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    sortingModel?.field,
    sortingModel?.sort,
    skip,
  ]);

  useEffect(() => {
    if (!isLoading) {
      if (data?.totalCount !== totalCount) {
        setTotalCount(data?.totalCount);
      }
    }
  }, [isLoading]);

  const handleCompanyChange = (event) => {
    console.log(event);
    // setAge(event.target.value as string);
  };

  const handleBlockUser = (value) => {
    blockUser(value || userInfo);
    setUserInfo(null);
    setIsOpenPopup(false);
  };

  const onClickBlockUser = (params) => {
    const info = {
      userInternalId: params?.row.id.split("/")[0],
      isBlocked: !params?.row.isBlocked,
    };
    if (!params?.row.isBlocked) {
      setUserInfo(info);
      setIsOpenPopup(true);
    } else {
      handleBlockUser(info);
    }
  };
  const onResetPasswordClick = (params) => {
    setUserInfo({
      userInternalId: params.row.id.split("/")[0],
    });
    setIsResetPasswodWindowOpen(true);
  };

  const handleResetPassword = (values) => {
    changePassword({
      userInternalId: userInfo.userInternalId,
      newPassword: values.password,
    });
    setUserInfo(null);
    setIsResetPasswodWindowOpen(false);
  };

  const copyToClipboard = (value) => {
    if (navigator?.clipboard) {
      navigator?.clipboard.writeText(value);
    }
  };
  const columns = [
    {
      field: "loginName",
      headerName: "Username",
      width: 100,
      editable: true,
    },
    {
      field: "email",
      headerName: "Email",
      width: 150,
      editable: true,
    },
    {
      field: "associationGroupId",
      headerName: "Insurance Association",
      width: 200,
      editable: true,
      flex: 1,
      renderCell: (params) => (
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">
            Association Group
          </InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={params.value || ""}
            label="Age"
            onChange={handleCompanyChange}
            disabled
          >
            {associations?.map((item, index) => (
              <MenuItem value={Number(item.id)} key={index}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      field: "memberName",
      headerName: "Insurance Company",
      width: 200,
    },
    {
      field: "roleId",
      headerName: "Role",
      width: 200,
      editable: true,
      flex: 1,
      renderCell: (params) => (
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">Role</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={params.value || ""}
            label="Role"
            onChange={handleCompanyChange}
            disabled
          >
            {roles?.map((item, index) => (
              <MenuItem value={Number(item.id)} key={index}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      field: "password",
      headerName: "Password",
      width: 140,
      renderCell: (params) => (
        <Box display={"flex"} style={{justifyContent:'space-between', width: '100%'}}>
          <p>{params.value}</p>
          {params.value && (
            <CopyIcon onClick={() => copyToClipboard(params.value)} />
          )}
        </Box>
      ),
    },
    {
      field: "isBlocked",
      headerName: "Block User",
      width: 60,
      renderCell: (params) => (
        <Checkbox
          label="block"
          size="lg"
          checked={params.value}
          onChange={() => {
            onClickBlockUser(params);
          }}
          disabled={userId === params.id}
        />
      ),
    },
    {
      field: "resetpassword",
      headerName: "",
      width: 150,
      editable: true,

      renderCell: (params) => (
        <ResetPassworButton onClick={() => onResetPasswordClick(params)}>
          Reset password
        </ResetPassworButton>
      ),
    },
  ];
  const fetchData = (value) => {
    const body = {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      orderBy: sortingModel?.field || "",
      orderByDirection: sortingModel?.sort || "",
      naicCode: value?.naicCode || "",
      email: value?.email || "",
    };
    getUsers(body);
  };
  const onApplyFilters = (value) => {
    if (paginationModel.page) {
      setPaginationModel((prev) => {
        return { ...prev, page: 0 };
      });
    } else {
      !skip && fetchData(value);
    }
  };

  return (
    <Box sx={{ height: "100%", width: "100%", margin: "50px auto 0 auto" }}>
      {isLoading && <LoadingPanel />}
      <Box display={"flex"} sx={{ justifyContent: "space-between" }} mb={3}>
        <Typography variant="h1" color="primary.main" sx={{ marginLeft: "16px" }}>
        User Management
        </Typography>
        <Box display={"flex"}>
          <Button
            variant="outlined"
            onClick={() => setIsOpenCreateUserPopup(true)}
          >
            Add User
          </Button>
          <TableFilter onApplyFilters={onApplyFilters} type="roleAssignment" />
        </Box>
      </Box>
      <StyledGrid
        data={data?.items}
        width="100%"
        columns={columns}
        rowCount={totalCount || 0}
        withFilter
        paginationModel={paginationModel}
        onSortingModelChange={(model) => setSortingModel(model[0])}
        setPaginationModel={setPaginationModel}
      />
      {isOpenCreateUserPopup && (
        <CreateUserPopup
          onClose={() => setIsOpenCreateUserPopup(false)}
          open={isOpenCreateUserPopup}
        />
      )}
      <InfoPopup
        isOpen={isOpenPopup}
        header="Block User"
        text="Are you sure you want to block the user?"
        handleClose={() => {
          setIsOpenPopup(false);
        }}
        onClick={() => handleBlockUser()}
      />
      <ModalWindow
        open={isResetPasswodWindowOpen}
        onClose={() => setIsResetPasswodWindowOpen(false)}
        header="Reset Password"
      >
        <Formik
          initialValues={{ password: "", confirmPassword: "" }}
          onSubmit={handleResetPassword}
          validationSchema={validationSchema}
        >
          {() => (
            <Form id={"memberForm"}>
              <FormContainer>
                <InputField
                  name={"password"}
                  label={"Password"}
                  mode="password"
                />
                <InputField
                  name={"confirmPassword"}
                  label={"Confirm Password"}
                  mode="password"
                />
              </FormContainer>
              <ButtonsContainer>
                <Button variant="outlined" type="submit">
                  Save
                </Button>
                <Button variant="outlined" type="reset">
                  Cancel
                </Button>
              </ButtonsContainer>
            </Form>
          )}
        </Formik>
      </ModalWindow>
    </Box>
  );
};

export default RoleAssignmentPage;
